import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const EMPTY_FORM = (hospital_id) => ({
  name: "", email: "", password: "", role: "user",
  hospital_id: hospital_id || "",
  can_delete_patients: false,
});

export default function StaffPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM(user?.hospital_id));

  const load = () => api.get("/users").then((r) => setUsers(r.data || []));
  useEffect(() => {
    load();
    if (user?.role === "superuser") api.get("/hospitals").then((r) => setHospitals(r.data || []));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (user.role !== "superuser") delete payload.hospital_id;
      await api.post("/users", payload);
      toast.success("Staff member added");
      setOpen(false);
      setForm(EMPTY_FORM(user?.hospital_id));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const toggleActive = async (u) => {
    const next = !(u.active === false);
    if (!window.confirm(`${next ? "Disable" : "Enable"} login for ${u.email}?`)) return;
    try {
      await api.patch(`/users/${u.id}`, { active: !next });
      toast.success(next ? "User disabled" : "User enabled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const toggleDeletePerm = async (u) => {
    const next = !u.can_delete_patients;
    try {
      await api.patch(`/users/${u.id}`, { can_delete_patients: next });
      toast.success(next ? "Delete-patients permission granted" : "Delete-patients permission revoked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="staff-page">
      <PageHeader
        eyebrow="Administration" title="Staff & Logins"
        subtitle="Add admins and staff users for your hospital. Roles: Admin (full hospital control) and User (clinical operations). Toggle 'Delete patients' to give a plain user permission to deactivate & permanently delete patients."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-btn" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                <Plus size={14} weight="bold" className="mr-1.5" /> New user
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-heading">Add staff</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="new-staff-form">
                <FieldStaff label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="s-name" colSpan={2} />
                <FieldStaff label="Email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="s-email" />
                <FieldStaff label="Password" required type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="s-pwd" />
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="s-role" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="user">User</SelectItem></SelectContent>
                  </Select>
                </div>
                {user?.role === "superuser" && (
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Hospital</Label>
                    <Select value={form.hospital_id} onValueChange={(v) => setForm({ ...form, hospital_id: v })}>
                      <SelectTrigger data-testid="s-hospital" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {hospitals.map((h) => (<SelectItem key={h.id} value={h.id}>{h.name} · {h.code}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2 flex items-center justify-between rounded-sm border border-[var(--hms-border-2)] bg-white px-3 py-2 mt-1">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Can delete patients</div>
                    <div className="text-[11px] text-[var(--hms-muted)]">Grants a non-admin user permission to deactivate & permanently delete patients.</div>
                  </div>
                  <Switch
                    checked={!!form.can_delete_patients}
                    onCheckedChange={(v) => setForm({ ...form, can_delete_patients: !!v })}
                    data-testid="s-can-delete"
                  />
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="submit" disabled={busy} data-testid="s-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                    {busy ? "Adding…" : "Add user"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {users.length === 0 ? (
        <EmptyState title="No staff yet" hint="Add your first admin or user to get started." />
      ) : (
        <div className="hms-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Active</th>
                <th className="text-left px-4 py-2.5">Delete patients</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === user.id;
                const isActive = u.active !== false;
                return (
                  <tr key={u.id} className="border-b border-[var(--hms-border)] hover:bg-[var(--hms-paper)]/50" data-testid={`staff-row-${u.email}`}>
                    <td className="px-4 py-2.5 font-medium">{u.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-2.5"><span className="font-heading text-[10px] uppercase tracking-wider font-bold">{u.role}</span></td>
                    <td className="px-4 py-2.5">
                      <Switch
                        checked={isActive}
                        disabled={isSelf}
                        onCheckedChange={() => toggleActive(u)}
                        data-testid={`staff-active-${u.email}`}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <Switch
                        checked={!!u.can_delete_patients}
                        disabled={isSelf || u.role === "admin" || u.role === "superuser"}
                        onCheckedChange={() => toggleDeletePerm(u)}
                        data-testid={`staff-can-delete-${u.email}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!isSelf && (
                        <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`staff-del-${u.email}`} className="rounded-sm text-[var(--hms-accent)]">
                          <Trash size={14} weight="bold" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FieldStaff({ label, value, onChange, required, type, testid, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}{required && " *"}</Label>
      <Input
        type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
      />
    </div>
  );
}
